/**
 * ChurchModule — Phase 1 admin module for a single church.
 *
 * Tabs:
 *   • Dashboard — at-a-glance counts, upcoming services, giving totals
 *   • Members   — directory CRUD (search, role/status filters, edit, delete)
 *   • Services  — schedule + attendance recording
 *
 * Mounted at /admin/churches/:churchId.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import TodaysReadingWidget from '@/components/TodaysReadingWidget';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import ReportIssueButton from '@/components/ReportIssueButton';
import BroadcastDashboard from '@/components/broadcast/BroadcastDashboard';
import { BroadcastMethods } from '@/components/live/BroadcastMethods';
import { BroadcastInfoEditor } from '@/components/live/BroadcastInfoEditor';
import VideoEditor from '@/components/broadcast/VideoEditor';
import VideoOutputPicker from '@/components/broadcast/VideoOutputPicker';
import RecordingsLibrary from '@/components/broadcast/RecordingsLibrary';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { useChurchRoles } from '@/lib/churchRoles';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Users, CalendarDays, Plus, Edit3, Trash2, ArrowLeft, Loader2, Search,
  CheckSquare, Heart, Church, BookOpen, Mic, Coins, FileAudio, Video,
  Radio, Copy, ExternalLink, ArrowRightLeft,
  Lock, KeyRound, Mail, Send, UserPlus,
  Layers, Megaphone, Check,
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import MemberOfficesDialog from '@/components/church/MemberOfficesDialog';
import { authHeaders } from '@/lib/authToken';
import { ChurchPublicPageEditor } from '@/components/church/ChurchPublicPageEditor';
import ChurchPledgesTab from '@/components/church/ChurchPledgesTab';
import ChurchTeamRolesTab from '@/components/church/ChurchTeamRolesTab';
import ApplicationsTab from '@/components/church/ApplicationsTab';
import ChurchQrPanel from '@/components/church/ChurchQrPanel';
import ChurchVisitDetails from '@/components/church/ChurchVisitDetails';
import TransferRequestsPanel from '@/components/church/TransferRequestsPanel';
import MemberPicker from '@/components/church/MemberPicker';
import AssignExistingMemberDialog from '@/components/church/AssignExistingMemberDialog';
import MemberTransferDialog from '@/components/church/MemberTransferDialog';
import { ServiceTimesManager } from '@/components/church/ServiceTimesManager';
import CurrencySelect from '@/components/CurrencySelect';
import ChurchPropertiesTab from '@/components/church/ChurchPropertiesTab';
import ChurchEventsTab from '@/components/church/ChurchEventsTab';
import { getCurrencyMeta } from '@/lib/currencies';
import VisitorsTab from '@/components/church/VisitorsTab';
import ScopedBiblePlans from '@/components/bible/ScopedBiblePlans';
import { MediaUpload } from '@/components/MediaUpload';
import { InviteRollDialog } from '@/components/church/InviteRollDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUSES = ['active', 'inactive', 'transferred', 'deceased'];

/** Every church office, grouped as the server returns it. */
const RoleOptions = ({ groups, testPrefix }) => groups.map((g) => (
  <SelectGroup key={g.key}>
    <SelectLabel className="text-[10px] uppercase tracking-wide text-slate-400">{g.label}</SelectLabel>
    {(g.roles || []).map((r) => (
      <SelectItem key={r.key} value={r.key} data-testid={`${testPrefix}-${r.key}`}>
        {r.label}
      </SelectItem>
    ))}
  </SelectGroup>
));

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.detail || `Request failed (${res.status})`);
  }
  return res.json();
};

/** Locked-but-visible tab body: says exactly which permission is missing. */
const Gate = ({ allowed, permission, children }) => {
  if (allowed) return children;
  return (
    <Card data-testid={`tab-locked-${permission.toLowerCase().replace(/ /g, '-')}`}>
      <CardContent className="py-14 text-center space-y-2">
        <Lock className="h-7 w-7 mx-auto text-slate-400" />
        <p className="text-sm text-slate-600">
          Needs <span className="font-semibold">{permission}</span> permission
        </p>
        <p className="text-xs text-slate-400">
          Ask a church Admin to add this permission to one of your roles.
        </p>
      </CardContent>
    </Card>
  );
};

const ChurchModule = () => {
  const { churchId } = useParams();
  const [searchParams] = useSearchParams();
  const { data: perms, error: permsError } = useQuery({
    queryKey: ['church-my-permissions', churchId],
    queryFn: () => api(`/api/church/member-rbac/${churchId}/my-permissions`),
    enabled: !!churchId,
    retry: false,
  });
  // The six role permissions decide which tabs open. Locked tabs stay visible
  // with a note so nobody wonders where a feature went.
  const can = (permission) => !perms || perms.is_admin
    || (perms.permissions || []).includes(permission);

  // iter 621 — the appointment that was activated to get here. Shown so the
  // holder can see which hat they are wearing and switch without signing out.
  const { data: activeAppointment } = useQuery({
    queryKey: ['active-appointment'],
    queryFn: () => api('/api/me/appointments/active'),
    retry: false,
    staleTime: 30_000,
  });
  const acting = activeAppointment?.active?.organisation_id === churchId
    ? activeAppointment.active : null;

  const { data: church } = useQuery({
    queryKey: ['church', churchId],
    queryFn: () => api(`/api/admin/churches/${churchId}`),
    enabled: !!churchId,
  });

  // A money-only appointment opens on its finance section (?section=offerings)
  // rather than a dashboard it cannot act on.
  const initialTab = searchParams.get('section') || 'dashboard';

  // iter 621 — typing another organisation's id into the address bar used to
  // render the whole workspace shell with empty tabs. The canonical permission
  // check grants nothing there (403, or 200 with an empty permission list), so
  // say so plainly instead of showing a hollow page, and never bounce to the
  // public directory.
  const noAuthorityHere = Boolean(permsError)
    || Boolean(perms && !perms.is_admin && (perms.permissions || []).length === 0);
  if (noAuthorityHere) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4"
           data-testid="workspace-access-denied">
        <div className="max-w-md text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-rose-100 flex items-center justify-center mb-4">
            <Church className="h-5 w-5 text-rose-700" />
          </div>
          <h1 className="text-xl font-bold text-slate-900">
            You have no appointment at this organisation
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            This workspace belongs to an organisation you do not hold a role in, so none of
            its records will open. If you administer it, ask a super admin to record your
            appointment.
          </p>
          <div className="flex items-center justify-center gap-2 mt-5">
            <Link to="/home">
              <Button variant="outline" size="sm" data-testid="access-denied-pick-appointment">
                Pick one of your appointments
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="church-module">
      <header className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-slate-400 hover:text-slate-700">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Church className="h-5 w-5 text-emerald-600" />
                {church?.name || 'Church'}
              </h1>
              <p className="text-xs text-slate-500">{church?.province ? `${church.province} · ` : ''}{church?.district || ''}</p>
              {acting && (
                <p className="text-xs text-emerald-700 mt-1" data-testid="acting-appointment">
                  Acting as <strong>{acting.title}</strong>
                  {acting.organisation_name ? ` · ${acting.organisation_name}` : ''}
                  {acting.country ? ` · ${acting.country}` : ''}
                  <Link to="/home" className="ml-2 underline"
                        data-testid="switch-appointment-link">Switch appointment</Link>
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ReportIssueButton scopeType="church" scopeId={church?.id} scopeLabel={church?.name} />
            {['hq', 'headquarters', 'country', 'national', 'province', 'district', 'region', 'zone']
              .includes((church?.level || '').toLowerCase()) && (
              <Link to={`/admin/churches/${churchId}/overview`}
                    className="text-xs text-emerald-700 hover:underline"
                    data-testid="church-grouping-overview-link">
                Grouping overview →
              </Link>
            )}
            {church?.slug && (
              <Link to={`/churches/${church.slug}`} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-700 hover:underline">
                View public page →
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <Tabs defaultValue={initialTab}>
          <TabsList className="grid grid-cols-3 sm:grid-cols-4 h-auto gap-1 md:inline-flex md:grid-cols-none">
            <TabsTrigger value="dashboard" data-testid="church-tab-dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="members" data-testid="church-tab-members">Members</TabsTrigger>
            <TabsTrigger value="applications" data-testid="church-tab-applications">Applications</TabsTrigger>
            <TabsTrigger value="visitors" data-testid="church-tab-visitors">Visitors</TabsTrigger>
            <TabsTrigger value="team" data-testid="church-tab-team">Team & Roles</TabsTrigger>
            <TabsTrigger value="services" data-testid="church-tab-services">Services</TabsTrigger>
            <TabsTrigger value="events" data-testid="church-tab-events">Events</TabsTrigger>
            <TabsTrigger value="offerings" data-testid="church-tab-offerings">Offerings</TabsTrigger>
            <TabsTrigger value="pledges" data-testid="church-tab-pledges">Pledges</TabsTrigger>
            <TabsTrigger value="sermons" data-testid="church-tab-sermons">Sermons</TabsTrigger>
            <TabsTrigger value="bible-plans" data-testid="church-tab-bible-plans">Reading Plans</TabsTrigger>
            <TabsTrigger value="live" data-testid="church-tab-live">Live</TabsTrigger>
            <TabsTrigger value="broadcast" data-testid="church-tab-broadcast">Broadcast</TabsTrigger>
            <TabsTrigger value="editor" data-testid="church-tab-editor">Editor</TabsTrigger>
            <TabsTrigger value="customize" data-testid="church-tab-customize">Customize</TabsTrigger>
            <TabsTrigger value="public-page" data-testid="church-tab-public-page">Public Page</TabsTrigger>
            {(church?.organisation_type === 'campus' || church?.owns_property) && (
              <TabsTrigger value="properties" data-testid="church-tab-properties">Properties</TabsTrigger>
            )}
          </TabsList>
          <TabsContent value="properties" className="mt-4">
            <Gate allowed={can('edit')} permission="Edit">
              <ChurchPropertiesTab churchId={churchId} />
            </Gate>
          </TabsContent>
          <TabsContent value="dashboard" className="mt-4"><DashboardTab churchId={churchId} /></TabsContent>
          <TabsContent value="members" className="mt-4">
            <Gate allowed={can('manage_members')} permission="Manage members">
              <MembersTab churchId={churchId} churchName={church?.name} />
            </Gate>
          </TabsContent>
          <TabsContent value="applications" className="mt-4">
            <div className="space-y-4">
              <TransferRequestsPanel churchId={churchId} />
              <ApplicationsTab churchId={churchId} church={church} />
            </div>
          </TabsContent>
          <TabsContent value="visitors" className="mt-4"><VisitorsTab churchId={churchId} /></TabsContent>
          <TabsContent value="team" className="mt-4"><ChurchTeamRolesTab churchId={churchId} /></TabsContent>
          <TabsContent value="services" className="mt-4"><ServicesTab churchId={churchId} /></TabsContent>
          <TabsContent value="events" className="mt-4"><ChurchEventsTab churchId={churchId} /></TabsContent>
          <TabsContent value="offerings" className="mt-4">
            <Gate allowed={can('manage_finance')} permission="Manage finance">
              <OfferingsTab churchId={churchId} homeCurrency={(church?.currency || 'USD').toUpperCase()} />
            </Gate>
          </TabsContent>
          <TabsContent value="pledges" className="mt-4">
            <Gate allowed={can('manage_finance')} permission="Manage finance">
              <ChurchPledgesTab churchId={churchId} homeCurrency={(church?.currency || 'USD').toUpperCase()} />
            </Gate>
          </TabsContent>
          <TabsContent value="sermons" className="mt-4"><SermonsTab churchId={churchId} /></TabsContent>
          <TabsContent value="bible-plans" className="mt-4">
            <ScopedBiblePlans scopeType="church" scopeId={churchId} isAdmin
                              tenantName={church?.name} />
          </TabsContent>
          <TabsContent value="live" className="mt-4"><LiveTab churchId={churchId} church={church} /></TabsContent>
          <TabsContent value="broadcast" className="mt-4">
            <BroadcastDashboard tenantId={churchId} linkModule="church_sermons" />
          </TabsContent>
          <TabsContent value="editor" className="mt-4">
            <VideoEditor tenantId={churchId} />
          </TabsContent>
          <TabsContent value="recordings" className="mt-4">
            <RecordingsLibrary tenantId={churchId} churchId={churchId} defaultModule="church_sermons" />
          </TabsContent>
          <TabsContent value="customize" className="mt-4"><CustomizeTab churchId={churchId} church={church} /></TabsContent>
          <TabsContent value="public-page" className="mt-4">
            <div className="space-y-4">
              <ChurchPublicPageEditor churchId={churchId} />
              <ChurchVisitDetails churchId={churchId} />
              <ChurchQrPanel churchId={churchId} />
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};


// ==================== DASHBOARD ====================
function DashboardTab({ churchId }) {
  const { data, isLoading } = useQuery({
    queryKey: ['church-dashboard', churchId],
    queryFn: () => api(`/api/church/${churchId}/dashboard`),
    enabled: !!churchId,
  });

  if (isLoading) return <div className="text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>;
  if (!data) return null;

  return (
    <div className="space-y-4">
      {/* Today's Bible reading — shared widget, uses church's pinned plan if configured */}
      <TodaysReadingWidget churchId={churchId} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-emerald-600 mb-1">
              <Users className="h-4 w-4" /><span className="text-xs uppercase tracking-wide font-medium">Members</span>
            </div>
            <p className="text-2xl font-bold text-slate-900" data-testid="church-members-total">{data.members.total}</p>
            <p className="text-xs text-slate-500">{data.members.active} active · people, not roles</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <CalendarDays className="h-4 w-4" /><span className="text-xs uppercase tracking-wide font-medium">Upcoming</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{(data.services.upcoming || []).length}</p>
            <p className="text-xs text-slate-500">services scheduled</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-violet-600 mb-1">
              <Heart className="h-4 w-4" /><span className="text-xs uppercase tracking-wide font-medium">Giving</span>
            </div>
            <p className="text-lg font-bold text-slate-900 truncate">
              {Object.entries(data.giving_totals || {}).length === 0 ? '—' : Object.entries(data.giving_totals).map(([c, v]) => `${c} ${v}`).join(' · ')}
            </p>
            <p className="text-xs text-slate-500">all time deposits</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-amber-600 mb-1">
              <BookOpen className="h-4 w-4" /><span className="text-xs uppercase tracking-wide font-medium">Roles</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{Object.keys(data.members.by_role || {}).length}</p>
            <p className="text-xs text-slate-500">distinct member roles</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Upcoming Services</CardTitle></CardHeader>
          <CardContent>
            {(data.services.upcoming || []).length === 0 ? (
              <p className="text-sm text-slate-400 italic">Nothing scheduled.</p>
            ) : (
              <ul className="space-y-2">
                {data.services.upcoming.map(s => (
                  <li key={s.id} className="flex justify-between text-sm">
                    <span className="text-slate-700"><b>{s.title}</b> · {s.service_date}{s.service_time && ` · ${s.service_time}`}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Recent Services</CardTitle></CardHeader>
          <CardContent>
            {(data.services.recent || []).length === 0 ? (
              <p className="text-sm text-slate-400 italic">No past services on file.</p>
            ) : (
              <ul className="space-y-2">
                {data.services.recent.map(s => (
                  <li key={s.id} className="flex justify-between text-sm">
                    <span className="text-slate-700"><b>{s.title}</b> · {s.service_date}</span>
                    <span className="text-xs text-emerald-700">{s.attendance_count || 0} attended</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Roles distribution</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {Object.keys(data.members.by_role || {}).length === 0 ? (
              <p className="text-sm text-slate-400 italic">No members yet.</p>
            ) : Object.entries(data.members.by_role).map(([role, count]) => (
              <Badge key={role} variant="outline" className="text-xs capitalize">
                {role}: <span className="ml-1 font-bold text-slate-700">{count}</span>
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


// ==================== MEMBERS ====================
function MembersTab({ churchId, churchName }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [editing, setEditing] = useState(null);
  const [transferring, setTransferring] = useState(null);
  const [showingOffices, setShowingOffices] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const { groups: roleGroups } = useChurchRoles();

  const { data: members = [], isLoading } = useQuery({
    queryKey: ['church-members', churchId, search, filterRole, filterStatus],
    queryFn: () => {
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      if (filterRole) params.set('role', filterRole);
      if (filterStatus) params.set('status', filterStatus);
      return api(`/api/church/${churchId}/members?${params.toString()}`);
    },
    enabled: !!churchId,
  });

  const delMut = useMutation({
    mutationFn: (id) => api(`/api/church/${churchId}/members/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Member removed'); qc.invalidateQueries({ queryKey: ['church-members', churchId] }); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-0 sm:min-w-[180px]">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email…" className="pl-8" data-testid="church-members-search" />
        </div>
        <Select value={filterRole || 'all'} onValueChange={(v) => setFilterRole(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-32" data-testid="church-members-filter-role"><SelectValue placeholder="All roles" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">All roles</SelectItem>
            <RoleOptions groups={roleGroups} testPrefix="church-members-filter-role" />
          </SelectContent>
        </Select>
        <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-36"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={() => setInviting(true)} data-testid="church-members-invite-roll">
          <Send className="h-4 w-4 mr-1" /> Invite to member area
        </Button>
        <Button variant="outline" onClick={() => setAssigning(true)} data-testid="church-members-assign-existing">
          <UserPlus className="h-4 w-4 mr-1" /> Add existing member
        </Button>
        <Button onClick={() => setEditing({ first_name: '', last_name: '', role: 'member', email: '', phone: '', whatsapp: '' })} data-testid="church-members-add">
          <Plus className="h-4 w-4 mr-1" /> Add member
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>
          ) : members.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No members yet. Click <b>Add member</b> to start your directory.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-slate-500 border-b">
                  <th className="text-left py-2 px-3">Name</th>
                  <th className="text-left py-2 px-3">Role</th>
                  <th className="text-left py-2 px-3">Email</th>
                  <th className="text-left py-2 px-3">Phone</th>
                  <th className="text-left py-2 px-3">Status</th>
                  <th className="text-left py-2 px-3">Member area</th>
                  <th className="text-right py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map(m => (
                  <tr key={m.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`church-member-row-${m.id}`}>
                    <td className="py-2 px-3 font-medium text-slate-800">{m.first_name} {m.last_name}</td>
                    <td className="py-2 px-3"><Badge variant="outline" className="text-xs capitalize">{m.role}</Badge></td>
                    <td className="py-2 px-3 text-slate-600 text-xs">{m.email || '—'}</td>
                    <td className="py-2 px-3 text-slate-600 text-xs">{m.phone || '—'}</td>
                    <td className="py-2 px-3"><Badge variant="outline" className={`text-[10px] capitalize ${m.status === 'active' ? 'text-emerald-700 border-emerald-200 bg-emerald-50' : 'text-slate-500'}`}>{m.status || 'active'}</Badge></td>
                    <td className="py-2 px-3"><MemberAreaBadge access={m.member_area} testId={`member-area-${m.id}`} /></td>
                    <td className="py-2 px-3 text-right">
                      <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => setEditing(m)} data-testid={`church-member-edit-${m.id}`}><Edit3 className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs" title="Every office this person holds across the platform" onClick={() => setShowingOffices(m)} data-testid={`church-member-offices-${m.id}`}><Layers className="h-3.5 w-3.5 mr-1" /> Offices</Button>
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50" title={`Transfer ${m.first_name} ${m.last_name} to another assembly`} onClick={() => setTransferring(m)} data-testid={`church-member-transfer-${m.id}`}><ArrowRightLeft className="h-3.5 w-3.5 mr-1" /> Transfer</Button>
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => { if (window.confirm(`Remove ${m.first_name}?`)) delMut.mutate(m.id); }} data-testid={`church-member-delete-${m.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" /> Remove</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {showingOffices && (
        <MemberOfficesDialog churchId={churchId} member={showingOffices}
                             onClose={() => setShowingOffices(null)} />
      )}

      {transferring && (
        <MemberTransferDialog churchId={churchId} member={transferring}
                              fromChurchName={churchName}
                              onClose={() => setTransferring(null)} />
      )}

      {inviting && (
        <InviteRollDialog churchId={churchId} churchName={churchName}
                          onClose={() => setInviting(false)} />
      )}

      {assigning && (
        <AssignExistingMemberDialog churchId={churchId} onClose={() => setAssigning(false)}
                                    onSaved={() => { qc.invalidateQueries({ queryKey: ['church-members', churchId] }); setAssigning(false); }} />
      )}
      {editing && <MemberEditor churchId={churchId} member={editing} onClose={() => setEditing(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['church-members', churchId] }); setEditing(null); }} />}
    </div>
  );
}

// Who can get into the member area, and who is still waiting on an invite.
const AREA_STYLE = {
  signed_in: 'text-emerald-700 border-emerald-200 bg-emerald-50',
  password_set: 'text-sky-700 border-sky-200 bg-sky-50',
  invited: 'text-amber-700 border-amber-200 bg-amber-50',
  not_invited: 'text-slate-500',
  no_email: 'text-slate-400 border-dashed',
};

function MemberAreaBadge({ access, testId }) {
  if (!access) return <span className="text-xs text-slate-400">—</span>;
  const when = access.last_login || access.invited_at;
  return (
    <span className="inline-flex flex-col gap-0.5" data-testid={testId}>
      <Badge variant="outline" className={`text-[10px] ${AREA_STYLE[access.state] || ''}`}>
        {access.label}
      </Badge>
      {when && (
        <span className="text-[10px] text-slate-400">{new Date(when).toLocaleDateString()}</span>
      )}
    </span>
  );
}


function MemberEditor({ churchId, member, onClose, onSaved }) {
  const isNew = !member.id;
  const [form, setForm] = useState({
    first_name: member.first_name || '',
    last_name: member.last_name || '',
    email: member.email || '',
    whatsapp: member.whatsapp || '',
    phone: member.phone || '',
    role: member.role || 'member',
    address: member.address || '',
    date_of_birth: member.date_of_birth || '',
    gender: member.gender || '',
    notes: member.notes || '',
    status: member.status || 'active',
    marital_status: member.marital_status || '',
    occupation: member.occupation || '',
    joined_date: member.joined_date || '',
    spiritual_birthday: member.spiritual_birthday || '',
    home_group_id: member.home_group_id || '',
    spouse_member_id: member.spouse_member_id || '',
    baptism: member.baptism || { baptized: false, baptism_date: '', baptism_place: '', baptism_type: '', baptized_by: '' },
    ministries: member.ministries || [],
  });
  const setBaptism = (k, v) => setForm(f => ({ ...f, baptism: { ...(f.baptism || {}), [k]: v } }));
  const addMinistry = () => setForm(f => ({ ...f, ministries: [...(f.ministries || []), { ministry_name: '', role: 'member', since: '' }] }));
  const updMinistry = (i, k, v) => setForm(f => ({ ...f, ministries: f.ministries.map((m, j) => j === i ? { ...m, [k]: v } : m) }));
  const delMinistry = (i) => setForm(f => ({ ...f, ministries: f.ministries.filter((_, j) => j !== i) }));

  const saveMut = useMutation({
    mutationFn: () => api(
      isNew ? `/api/church/${churchId}/members` : `/api/church/${churchId}/members/${member.id}`,
      { method: isNew ? 'POST' : 'PUT', body: JSON.stringify(form) },
    ),
    onSuccess: (res) => {
      const w = res?.welcome_email;
      toast.success(isNew ? 'Member added' : 'Member updated');
      if (isNew && w) {
        if (w.sent) toast.info(`Welcome email sent to ${w.to}`);
        else if (w.queued) toast.info('Welcome email queued — it sends as soon as email is switched on');
        else if (w.reason) toast.info(`Welcome email not sent — ${w.reason}`);
      }
      onSaved?.();
    },
    onError: (e) => toast.error(e.message),
  });

  const mailToast = (r, whenSent, whenQueued) => {
    if (r?.sent) toast.success(whenSent);
    else if (r?.queued) toast.success(whenQueued);
    else toast.error(r?.reason || 'Email could not be sent');
  };

  const resetMut = useMutation({
    mutationFn: () => api(`/api/church/${churchId}/members/${member.id}/send-password-reset`,
                          { method: 'POST' }),
    onSuccess: (res) => mailToast(
      res?.password_reset_email,
      res?.first_time ? 'Set-password link sent' : 'Password reset link sent',
      'Reset link queued — it sends as soon as email is switched on'),
    onError: (e) => toast.error(e.message),
  });

  const welcomeMut = useMutation({
    mutationFn: () => api(`/api/church/${churchId}/members/${member.id}/resend-welcome`,
                          { method: 'POST' }),
    onSuccess: (res) => mailToast(
      res?.welcome_email, 'Welcome email sent again',
      'Welcome email queued — it sends as soon as email is switched on'),
    onError: (e) => toast.error(e.message),
  });

  const [credentials, setCredentials] = useState(null);
  const { groups: roleGroups, roleKeys } = useChurchRoles();
  // Custom roles a Super Admin built under Church Roles, plus anything this
  // church added itself — they sit alongside the locked standard roles.
  const { data: rolesData } = useQuery({
    queryKey: ['church-roles', churchId],
    queryFn: () => api(`/api/church/member-rbac/${churchId}/roles`),
    enabled: !!churchId,
    retry: false,
  });
  const customRoles = (rolesData?.roles || [])
    .filter(r => r.is_global || !r.is_system)
    .map(r => r.name)
    .filter(n => n && !roleKeys.includes(n.toLowerCase()));

  const tempMut = useMutation({
    mutationFn: () => api(`/api/church/${churchId}/members/${member.id}/reset-password`,
                          { method: 'POST' }),
    onSuccess: (res) => {
      setCredentials(res);
      if (res?.email?.sent) toast.success(`Temporary password emailed to ${res.email.to}`);
      else if (res?.email?.queued) toast.success('Temporary password queued — it sends once email is switched on');
      else toast.success('Temporary password created — pass it on to the member');
    },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="church-member-editor">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Add member' : `Edit · ${member.first_name} ${member.last_name}`}</DialogTitle>
          <DialogDescription>{isNew ? 'Only a first and last name are needed — email, phone and WhatsApp can be added later.' : 'Update contact info, baptism, ministries or status.'}</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid grid-cols-3 sm:grid-cols-4 h-auto gap-1 md:inline-flex md:grid-cols-none">
            <TabsTrigger value="basic" data-testid="member-tab-basic">Basic</TabsTrigger>
            <TabsTrigger value="baptism" data-testid="member-tab-baptism">Baptism</TabsTrigger>
            <TabsTrigger value="family" data-testid="member-tab-family">Family</TabsTrigger>
            <TabsTrigger value="ministries" data-testid="member-tab-ministries">Ministries</TabsTrigger>
          </TabsList>
          <TabsContent value="basic" className="mt-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>First name *</Label><Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} data-testid="church-member-first" /></div>
              <div><Label>Last name *</Label><Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} data-testid="church-member-last" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Optional — add later" data-testid="church-member-email" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Optional" data-testid="church-member-phone" /></div>
              <div><Label>WhatsApp</Label><Input value={form.whatsapp} onChange={e => setForm({ ...form, whatsapp: e.target.value })} placeholder="Optional — e.g. +263 77 123 4567" data-testid="church-member-whatsapp" /></div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="church-member-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <RoleOptions groups={roleGroups} testPrefix="church-member-role" />
                    {customRoles.length > 0 && (
                      <SelectGroup>
                        <SelectLabel className="text-[10px] uppercase tracking-wide text-slate-400">This church's own roles</SelectLabel>
                        {customRoles.map(r => (
                          <SelectItem key={r} value={r} data-testid={`church-member-role-custom-${r.toLowerCase().replace(/ /g, '-')}`}>
                            {r} <span className="ml-1 text-[9px] uppercase tracking-wide text-emerald-700">Custom</span>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Date of birth</Label><Input type="date" value={(form.date_of_birth || '').slice(0,10)} onChange={e => setForm({ ...form, date_of_birth: e.target.value })} /></div>
              <div>
                <Label>Gender</Label>
                <Select value={form.gender || 'unspecified'} onValueChange={(v) => setForm({ ...form, gender: v === 'unspecified' ? '' : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unspecified">—</SelectItem>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Marital status</Label>
                <Select value={form.marital_status || 'unspecified'} onValueChange={v => setForm({ ...form, marital_status: v === 'unspecified' ? '' : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unspecified">—</SelectItem>
                    <SelectItem value="single">Single</SelectItem>
                    <SelectItem value="married">Married</SelectItem>
                    <SelectItem value="widowed">Widowed</SelectItem>
                    <SelectItem value="divorced">Divorced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Occupation</Label><Input value={form.occupation} onChange={e => setForm({ ...form, occupation: e.target.value })} /></div>
              <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
              <div><Label>Joined the church</Label><Input type="date" value={(form.joined_date || '').slice(0,10)} onChange={e => setForm({ ...form, joined_date: e.target.value })} /></div>
              <div><Label>Spiritual birthday</Label><Input type="date" value={(form.spiritual_birthday || '').slice(0,10)} onChange={e => setForm({ ...form, spiritual_birthday: e.target.value })} /></div>
              <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
          </TabsContent>
          <TabsContent value="baptism" className="mt-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="member-baptized"
                  checked={!!form.baptism?.baptized}
                  onChange={e => setBaptism('baptized', e.target.checked)}
                  data-testid="member-baptized"
                />
                <Label htmlFor="member-baptized">This person has been baptised</Label>
              </div>
              <div><Label>Baptism date</Label><Input type="date" value={(form.baptism?.baptism_date || '').slice(0,10)} onChange={e => setBaptism('baptism_date', e.target.value)} data-testid="member-baptism-date" /></div>
              <div><Label>Baptism place</Label><Input value={form.baptism?.baptism_place || ''} onChange={e => setBaptism('baptism_place', e.target.value)} placeholder="Church or location" data-testid="member-baptism-place" /></div>
              <div>
                <Label>Baptism type</Label>
                <Select value={form.baptism?.baptism_type || 'unspecified'} onValueChange={v => setBaptism('baptism_type', v === 'unspecified' ? '' : v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unspecified">—</SelectItem>
                    <SelectItem value="water">Water</SelectItem>
                    <SelectItem value="spirit">Holy Spirit</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Baptised by</Label><Input value={form.baptism?.baptized_by || ''} onChange={e => setBaptism('baptized_by', e.target.value)} placeholder="Minister name" data-testid="member-baptism-by" /></div>
            </div>
          </TabsContent>
          <TabsContent value="family" className="mt-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <Label>Home group ID</Label>
                <Input value={form.home_group_id} onChange={e => setForm({ ...form, home_group_id: e.target.value })} placeholder="Paste a home-group church id" data-testid="member-home-group-id" />
                <p className="text-[10px] text-slate-400 mt-1">Use the church id of a record whose level is "Home Group". Browseable picker coming in Phase 3.</p>
              </div>
              <div className="col-span-2"><Label>Spouse member ID</Label><Input value={form.spouse_member_id} onChange={e => setForm({ ...form, spouse_member_id: e.target.value })} data-testid="member-spouse-id" /></div>
            </div>
          </TabsContent>
          <TabsContent value="ministries" className="mt-3">
            <div className="space-y-2">
              {(form.ministries || []).length === 0 && (
                <p className="text-xs text-slate-400">No ministries yet.</p>
              )}
              {(form.ministries || []).map((m, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end bg-slate-50 rounded-md p-2">
                  <div className="col-span-5"><Label className="text-[10px]">Ministry</Label><Input value={m.ministry_name} onChange={e => updMinistry(i, 'ministry_name', e.target.value)} placeholder="Worship / Youth / Ushers..." data-testid={`member-ministry-name-${i}`} /></div>
                  <div className="col-span-3"><Label className="text-[10px]">Role</Label><Input value={m.role || ''} onChange={e => updMinistry(i, 'role', e.target.value)} placeholder="member / leader" /></div>
                  <div className="col-span-3"><Label className="text-[10px]">Since</Label><Input type="date" value={(m.since || '').slice(0,10)} onChange={e => updMinistry(i, 'since', e.target.value)} /></div>
                  <Button variant="ghost" size="icon" className="col-span-1 text-red-600 h-9 w-9" onClick={() => delMinistry(i)} data-testid={`member-ministry-del-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={addMinistry} data-testid="member-ministry-add"><Plus className="h-3.5 w-3.5 mr-1" /> Add ministry</Button>
            </div>
          </TabsContent>
        </Tabs>
        {!isNew && (
          <div className="border-t pt-3 mt-1" data-testid="member-account-actions">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 mb-2">
              Member area access
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {form.email && (
                <>
                  <Button size="sm" variant="outline" disabled={resetMut.isPending}
                          onClick={() => resetMut.mutate()} data-testid="member-reset-password">
                    {resetMut.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      : <KeyRound className="h-3.5 w-3.5 mr-1.5" />}
                    Email reset link
                  </Button>
                  <Button size="sm" variant="outline" disabled={welcomeMut.isPending}
                          onClick={() => welcomeMut.mutate()} data-testid="member-resend-welcome">
                    {welcomeMut.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      : <Mail className="h-3.5 w-3.5 mr-1.5" />}
                    Resend welcome email
                  </Button>
                </>
              )}
              <Button size="sm" variant="outline" disabled={tempMut.isPending}
                      onClick={() => tempMut.mutate()} data-testid="member-temp-password">
                {tempMut.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  : <KeyRound className="h-3.5 w-3.5 mr-1.5" />}
                Set a temporary password
              </Button>
              <span className="text-xs text-slate-400">
                {form.email ? `Sends to ${form.email}`
                  : 'No email on file — the temporary password shows here for you to pass on'}
              </span>
            </div>
            {credentials && (
              <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2"
                   data-testid="member-temp-credentials">
                <p className="text-xs text-amber-900">
                  Shown once only. The member must change this password after signing in.
                </p>
                <div className="grid gap-1 text-xs font-mono text-slate-800">
                  <span data-testid="member-temp-login">Sign in as: {credentials.login_email}</span>
                  <span data-testid="member-temp-value">Password: {credentials.temp_password}</span>
                </div>
                {credentials.generated_handle && (
                  <p className="text-[11px] text-amber-800">
                    This member has no email, so we created a sign-in name for them. Add a real
                    email later and they can use that instead.
                  </p>
                )}
                <Button size="sm" variant="outline" data-testid="member-temp-copy"
                        onClick={() => {
                          navigator.clipboard?.writeText(
                            `${credentials.login_url}\nSign in as: ${credentials.login_email}\nPassword: ${credentials.temp_password}`);
                          toast.success('Login details copied');
                        }}>
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy login details
                </Button>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => {
              if (!form.first_name || !form.last_name) return toast.error('First & last name required');
              saveMut.mutate();
            }}
            disabled={saveMut.isPending}
            data-testid="church-member-save"
          >
            {saveMut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            {isNew ? 'Add member' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ==================== SERVICES ====================
function ServicesTab({ churchId }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [attendanceFor, setAttendanceFor] = useState(null);

  const { data: services = [], isLoading } = useQuery({
    queryKey: ['church-services', churchId],
    queryFn: () => api(`/api/church/${churchId}/services`),
    enabled: !!churchId,
  });
  const delMut = useMutation({
    mutationFn: (id) => api(`/api/church/${churchId}/services/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Service deleted'); qc.invalidateQueries({ queryKey: ['church-services', churchId] }); },
    onError: (e) => toast.error(e.message),
  });

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-3">
      <Card data-testid="church-weekly-services-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Weekly service times</CardTitle>
          <CardDescription>
            Set the rhythm once. Public ones show on this assembly's page and fill out
            its province page.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ServiceTimesManager churchId={churchId} />
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Service schedule</h2>
        <Button onClick={() => setEditing({ title: 'Sunday Service', service_date: today, service_time: '10:00', location: '', notes: '' })} data-testid="church-services-add">
          <Plus className="h-4 w-4 mr-1" /> Schedule service
        </Button>
      </div>
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>
          ) : services.length === 0 ? (
            <div className="p-12 text-center">
              <CalendarDays className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No services yet. Schedule your first service to start tracking attendance.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-slate-500 border-b">
                  <th className="text-left py-2 px-3">Title</th>
                  <th className="text-left py-2 px-3">Date</th>
                  <th className="text-left py-2 px-3">Time</th>
                  <th className="text-left py-2 px-3">Location</th>
                  <th className="text-right py-2 px-3">Attendance</th>
                  <th className="text-right py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {services.map(s => (
                  <tr key={s.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`church-service-row-${s.id}`}>
                    <td className="py-2 px-3 font-medium text-slate-800">{s.title}</td>
                    <td className="py-2 px-3 text-slate-600 text-xs">{s.service_date}</td>
                    <td className="py-2 px-3 text-slate-600 text-xs">{s.service_time || '—'}</td>
                    <td className="py-2 px-3 text-slate-600 text-xs">{s.location || '—'}</td>
                    <td className="py-2 px-3 text-right">
                      <button onClick={() => setAttendanceFor(s)} className="text-xs text-emerald-700 hover:underline" data-testid={`church-service-attendance-${s.id}`}>
                        {s.attendance_count || 0}<CheckSquare className="h-3 w-3 inline ml-1" />
                      </button>
                    </td>
                    <td className="py-2 px-3 text-right">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing(s)} data-testid={`church-service-edit-${s.id}`}><Edit3 className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => { if (window.confirm(`Delete ${s.title}?`)) delMut.mutate(s.id); }} data-testid={`church-service-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {editing && <ServiceEditor churchId={churchId} service={editing} onClose={() => setEditing(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['church-services', churchId] }); setEditing(null); }} />}
      {attendanceFor && <AttendanceDialog churchId={churchId} service={attendanceFor} onClose={() => setAttendanceFor(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['church-services', churchId] }); setAttendanceFor(null); }} />}
    </div>
  );
}


function ServiceEditor({ churchId, service, onClose, onSaved }) {
  const isNew = !service.id;
  const [form, setForm] = useState({
    title: service.title || '',
    service_date: service.service_date || '',
    service_time: service.service_time || '',
    location: service.location || '',
    notes: service.notes || '',
  });
  const saveMut = useMutation({
    mutationFn: () => api(
      isNew ? `/api/church/${churchId}/services` : `/api/church/${churchId}/services/${service.id}`,
      { method: isNew ? 'POST' : 'PUT', body: JSON.stringify(form) },
    ),
    onSuccess: () => { toast.success(isNew ? 'Service scheduled' : 'Service updated'); onSaved?.(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="church-service-editor">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Schedule a service' : `Edit · ${service.title}`}</DialogTitle>
          <DialogDescription>When and where the service happens.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Sunday Service" data-testid="church-service-title" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Date *</Label><Input type="date" value={form.service_date} onChange={e => setForm({ ...form, service_date: e.target.value })} data-testid="church-service-date" /></div>
            <div><Label>Time</Label><Input type="time" value={form.service_time} onChange={e => setForm({ ...form, service_time: e.target.value })} data-testid="church-service-time" /></div>
          </div>
          <div><Label>Location</Label><Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} /></div>
          <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { if (!form.title || !form.service_date) return toast.error('Title & date required'); saveMut.mutate(); }} disabled={saveMut.isPending} data-testid="church-service-save">
            {saveMut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            {isNew ? 'Schedule' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function AttendanceDialog({ churchId, service, onClose, onSaved }) {
  const [count, setCount] = useState(service.attendance_count || 0);
  const saveMut = useMutation({
    mutationFn: () => api(`/api/church/${churchId}/services/${service.id}/attendance`, {
      method: 'POST', body: JSON.stringify({ count: Number(count) }),
    }),
    onSuccess: () => { toast.success('Attendance recorded'); onSaved?.(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="church-attendance-dialog">
        <DialogHeader>
          <DialogTitle>Attendance · {service.title}</DialogTitle>
          <DialogDescription>{service.service_date}{service.service_time && ` · ${service.service_time}`}</DialogDescription>
        </DialogHeader>
        <div>
          <Label>Headcount</Label>
          <Input type="number" min={0} value={count} onChange={e => setCount(e.target.value)} data-testid="church-attendance-count" />
          <p className="text-xs text-slate-500 mt-1">A quick tally — per-member roster comes in a later phase.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending} data-testid="church-attendance-save">
            {saveMut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ==================== OFFERINGS ====================
const OFFERING_CATEGORIES = ['tithe', 'offering', 'thanksgiving', 'missions', 'building', 'youth', 'welfare', 'special', 'other'];
const PAYMENT_METHODS = ['cash', 'card', 'mobile', 'bank', 'other'];

function OfferingsTab({ churchId, homeCurrency = 'USD' }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [linking, setLinking] = useState(null);
  const [reportingCurrency, setReportingCurrency] = useState(homeCurrency);
  const [reviewOnly, setReviewOnly] = useState(false);
  const { data: offerings = [], isLoading } = useQuery({
    queryKey: ['church-offerings', churchId, reviewOnly],
    queryFn: () => api(`/api/church/${churchId}/offerings${reviewOnly ? '?needs_campaign_review=true' : ''}`),
  });
  const { data: reviewCount = { count: 0 } } = useQuery({
    queryKey: ['church-offerings-review-count', churchId],
    queryFn: () => api(`/api/church/${churchId}/offerings/campaign-review-count`),
  });
  const { data: summary = { rows: [], totals_by_currency: {} } } = useQuery({
    queryKey: ['church-offerings-summary', churchId, reportingCurrency],
    queryFn: () => api(`/api/church/${churchId}/offerings/summary?days=90&reporting_currency=${reportingCurrency}`),
  });
  const del = useMutation({
    mutationFn: (id) => api(`/api/church/${churchId}/offerings/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['church-offerings', churchId] }); qc.invalidateQueries({ queryKey: ['church-offerings-summary', churchId] }); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-slate-500">{offerings.length} entries · 90-day window summary below</p>
        <div className="flex items-center gap-2">
          {(reviewCount.count > 0 || reviewOnly) && (
            <Button variant={reviewOnly ? 'default' : 'outline'} size="sm"
                    className={reviewOnly ? '' : 'border-amber-300 text-amber-800'}
                    onClick={() => setReviewOnly(v => !v)} data-testid="offering-review-filter">
              <Megaphone className="h-4 w-4 mr-1" />
              Needs campaign review{reviewCount.count > 0 ? ` (${reviewCount.count})` : ''}
            </Button>
          )}
          <Button onClick={() => setAdding(true)} data-testid="offering-add"><Plus className="h-4 w-4 mr-1" />Record Offering</Button>
        </div>
      </div>

      {/* 90-day summary tiles */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <CardTitle className="text-base flex items-center gap-2"><Coins className="h-4 w-4 text-amber-600" />Last 90 days · totals by currency</CardTitle>
            <div className="w-44">
              <CurrencySelect
                value={reportingCurrency}
                onChange={setReportingCurrency}
                showHomeHint={false}
                testId="offering-reporting-currency"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {Object.keys(summary.totals_by_currency || {}).length === 0 ? (
            <p className="text-sm text-slate-400">No offerings recorded yet.</p>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-3">
                {Object.entries(summary.totals_by_currency).map(([c, total]) => (
                  <div key={c} className="px-4 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-900" data-testid={`offering-total-${c}`}>
                    <p className="text-xs uppercase tracking-wide text-amber-700">{c} · {summary.counts_by_currency?.[c] || 0} gifts</p>
                    <p className="text-lg font-bold">{getCurrencyMeta(c).symbol} {Number(total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                  </div>
                ))}
                {summary.rows.slice(0, 6).map(r => (
                  <div key={`${r.category}-${r.currency}`} className="px-3 py-2 rounded-lg bg-slate-50 border text-slate-700 text-xs">
                    <p className="capitalize font-medium">{r.category}</p>
                    <p>{r.currency} {Number(r.total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                  </div>
                ))}
              </div>
              {summary.converted && (
                <p className="text-xs text-slate-500" data-testid="offering-converted-total">
                  Indicative combined total ≈ <span className="font-semibold text-slate-700">{summary.converted.currency} {Number(summary.converted.grand_total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  {summary.converted.unconverted?.length > 0 && ` · no rate for ${summary.converted.unconverted.join(', ')}`}
                  {' '}— per-currency figures above are authoritative.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? <div className="p-8 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>
           : offerings.length === 0 ? <div className="p-12 text-center text-slate-400"><Coins className="h-10 w-10 mx-auto mb-2" /><p>No offerings yet.</p></div>
           : <table className="w-full text-sm">
              <thead><tr className="text-xs uppercase tracking-wide text-slate-500 border-b">
                <th className="text-left py-2 px-3">Date</th><th className="text-left py-2 px-3">Category</th><th className="text-left py-2 px-3">Contributor</th><th className="text-right py-2 px-3">Amount</th><th className="text-left py-2 px-3">Method</th><th className="text-right py-2 px-3"></th>
              </tr></thead>
              <tbody>
                {offerings.map(o => (
                  <tr key={o.id} className="border-b last:border-0" data-testid={`offering-row-${o.id}`}>
                    <td className="py-2 px-3 text-xs">{o.recorded_for}</td>
                    <td className="py-2 px-3"><Badge variant="outline" className="text-[10px] capitalize">{o.category}</Badge></td>
                    <td className="py-2 px-3 text-xs">
                      {o.contributor_name || <span className="text-slate-400 italic">anonymous</span>}
                      {/* A tick means this gift is attached to the roll and
                          counts towards that member's giving history; a bare
                          name is not attributed to anyone. */}
                      {o.member_id
                        ? <Check className="ml-1 inline h-3 w-3 align-text-top text-emerald-600" title="Linked to the member roll" data-testid={`offering-linked-${o.id}`} />
                        : o.contributor_name
                          ? <span className="ml-1 text-[10px] text-slate-400" title="Typed name, not attached to a member">· unlinked</span>
                          : null}
                    </td>
                    <td className="py-2 px-3 text-right font-medium text-amber-700">{o.currency} {Number(o.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    <td className="py-2 px-3 text-xs text-slate-500 capitalize">{o.payment_method || '—'}</td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">
                      {(o.campaign_ids?.length || 0) > 0 ? (
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px] text-emerald-700"
                                onClick={() => setLinking(o)} data-testid={`offering-campaign-linked-${o.id}`}>
                          <Megaphone className="h-3 w-3 mr-1" />
                          On {o.campaign_ids.length === 1 ? 'a campaign' : `${o.campaign_ids.length} campaigns`}
                        </Button>
                      ) : (o.campaign_match_count || 0) > 0 ? (
                        <Button variant="outline" size="sm" className="h-7 px-2 text-[11px] border-amber-300 text-amber-800"
                                onClick={() => setLinking(o)} data-testid={`offering-campaign-apply-${o.id}`}>
                          <Megaphone className="h-3 w-3 mr-1" />
                          {o.campaign_review_needed ? 'Confirm campaign' : 'Add to campaign?'}
                        </Button>
                      ) : null}
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600 ml-1" onClick={() => { if (window.confirm('Delete this offering?')) del.mutate(o.id); }} data-testid={`offering-del-${o.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>}
        </CardContent>
      </Card>
      {adding && <OfferingDialog churchId={churchId} homeCurrency={homeCurrency} onClose={() => setAdding(false)} onSaved={() => { qc.invalidateQueries({ queryKey: ['church-offerings', churchId] }); qc.invalidateQueries({ queryKey: ['church-offerings-summary', churchId] }); setAdding(false); }} />}
      {linking && <OfferingCampaignDialog churchId={churchId} offering={linking} onClose={() => setLinking(null)}
                                          onSaved={() => { qc.invalidateQueries({ queryKey: ['church-offerings', churchId] }); qc.invalidateQueries({ queryKey: ['church-offerings-review-count', churchId] }); setLinking(null); }} />}
    </div>
  );
}

// Applying an already-recorded gift (usually an online one, where the donor
// chose the designation at checkout) to the campaigns it matches.
function OfferingCampaignDialog({ churchId, offering, onClose, onSaved }) {
  const [picked, setPicked] = useState(offering.campaign_ids || []);
  const { data, isLoading } = useQuery({
    queryKey: ['offering-campaign-matches', churchId, offering.category, offering.notes],
    queryFn: () => api(`/api/church/${churchId}/offerings/campaign-matches?category=${encodeURIComponent(offering.category || 'offering')}&notes=${encodeURIComponent(offering.notes || '')}`),
  });
  const matches = data?.matches || [];
  const save = useMutation({
    mutationFn: () => api(`/api/church/${churchId}/offerings/${offering.id}/campaigns`, {
      method: 'POST', body: JSON.stringify({ campaign_ids: picked }),
    }),
    onSuccess: (res) => {
      if (res.campaigns?.length) toast.success(`Counted towards ${res.campaigns.map(c => c.name).join(', ')}`);
      else if (res.removed?.length) toast.success('Taken off the campaign totals');
      else toast.success('No campaign linked');
      onSaved?.();
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="offering-campaign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-amber-600" />Apply this gift to a campaign
          </DialogTitle>
          <DialogDescription>
            {offering.currency} {Number(offering.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })} ·{' '}
            <span className="capitalize">{offering.category}</span>
            {offering.contributor_name ? ` · ${offering.contributor_name}` : ''}.
            The church record stays as it is; the amount is added to whichever campaign totals you tick.
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="py-8 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Looking for matches…</div>
        ) : matches.length === 0 ? (
          <p className="py-6 text-sm text-slate-500" data-testid="offering-campaign-none">
            No live campaign matches this designation — it records as a normal offering.
          </p>
        ) : (
          <div className="space-y-1.5">
            {matches.map(m => (
              <label key={m.id} className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded hover:bg-slate-50"
                     data-testid={`offering-campaign-option-${m.id}`}>
                <Checkbox checked={picked.includes(m.id)}
                          onCheckedChange={() => setPicked(p => p.includes(m.id) ? p.filter(x => x !== m.id) : [...p, m.id])}
                          className="mt-0.5" />
                <span className="min-w-0">
                  <span className="font-medium">{m.name}</span>
                  <span className="block text-[11px] text-slate-500">
                    matched on “{m.matched_on}” · {m.scope}
                  </span>
                </span>
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || matches.length === 0}
                  data-testid="offering-campaign-save">
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/** Every live campaign this church may give to, searched in the database.
 *
 *  iter 699 — the form only ever offered the keyword *suggestions*, so a gift
 *  whose category matched nothing showed one option or none. This is the
 *  "show me all of them" picker: search by campaign, project, institution or
 *  assembly, and nothing is selected unless the recorder selects it. */
function CampaignPicker({ churchId, picked, onToggle, onClear }) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');

  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(t);
  }, [term]);

  const { data, isFetching } = useQuery({
    queryKey: ['offering-campaign-options', churchId, debounced],
    queryFn: () => api(`/api/church/${churchId}/campaign-options?q=${encodeURIComponent(debounced)}&limit=50`),
    enabled: open,
    staleTime: 30000,
  });
  const items = data?.items || [];
  const chosen = items.filter(c => picked.includes(c.id));

  return (
    <div data-testid="offering-campaign-picker">
      <Label>Campaign (optional)</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button" variant="outline"
            className="w-full justify-between font-normal"
            data-testid="offering-campaign-picker-trigger"
          >
            <span className="truncate text-left">
              {picked.length === 0
                ? 'No campaign — general offering'
                : (chosen.length
                    ? chosen.map(c => c.name).join(', ')
                    : `${picked.length} campaign${picked.length > 1 ? 's' : ''} selected`)}
            </span>
            <Megaphone className="h-3.5 w-3.5 opacity-50 shrink-0 ml-2" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search campaign, project, institution or assembly…"
              value={term} onValueChange={setTerm}
              data-testid="offering-campaign-search"
            />
            <CommandList>
              {isFetching && items.length === 0 ? (
                <div className="py-6 text-center text-xs text-slate-400">
                  <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                </div>
              ) : (
                <>
                  <CommandEmpty>No live campaign matches that.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value="__none__" onSelect={onClear}
                      data-testid="offering-campaign-option-none"
                    >
                      <Check className={`mr-2 h-3.5 w-3.5 ${picked.length === 0 ? 'opacity-100' : 'opacity-0'}`} />
                      <span>No campaign — general offering</span>
                    </CommandItem>
                  </CommandGroup>
                  {items.length > 0 && (
                    <CommandGroup heading="Live campaigns">
                      {items.map(c => (
                        <CommandItem
                          key={c.id} value={c.id}
                          onSelect={() => onToggle(c.id)}
                          data-testid={`offering-campaign-pick-${c.id}`}
                        >
                          <Check className={`mr-2 h-3.5 w-3.5 shrink-0 ${picked.includes(c.id) ? 'opacity-100' : 'opacity-0'}`} />
                          <span className="min-w-0">
                            <span className="font-medium block truncate">{c.name}</span>
                            <span className="block text-[11px] text-slate-500 truncate">
                              {c.owner_label}
                              {c.target_amount > 0 && ` · ${c.currency} ${Number(c.target_amount).toLocaleString()} goal`}
                            </span>
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                  {data?.truncated && (
                    <div className="px-3 py-2 text-[11px] text-slate-400 border-t">
                      Showing the first {items.length}. Keep typing to narrow it down.
                    </div>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <p className="text-[10px] text-slate-400 mt-0.5">
        The gift is recorded once on the church ledger and counted towards any campaign chosen.
      </p>
    </div>
  );
}


function OfferingDialog({ churchId, homeCurrency = 'USD', onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    amount: '', currency: homeCurrency, category: 'offering',
    member_id: '', contributor_name: '',
    contributor_email: '', contributor_phone: '', payment_method: 'cash',
    recorded_for: today, notes: '', send_receipt_via: 'none',
  });
  const [picked, setPicked] = useState([]);
  const { data: typesData } = useQuery({
    queryKey: ['offering-donation-types', churchId],
    queryFn: () => api(`/api/church/${churchId}/donation-types`),
  });
  const campaignTypes = typesData?.campaigns || [];
  const standardTypes = typesData?.standard?.map(s => s.value) || OFFERING_CATEGORIES;
  const pickedCampaignType = String(form.category || '').startsWith('campaign:');

  const { data: matchData } = useQuery({
    queryKey: ['offering-campaign-matches', churchId, form.category, form.notes],
    enabled: !pickedCampaignType,
    queryFn: () => api(`/api/church/${churchId}/offerings/campaign-matches?category=${encodeURIComponent(form.category)}&notes=${encodeURIComponent(form.notes || '')}`),
  });
  const matches = pickedCampaignType ? [] : (matchData?.matches || []);
  const toggleCampaign = (id) => setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const mut = useMutation({
    mutationFn: () => api(`/api/church/${churchId}/offerings`, { method: 'POST', body: JSON.stringify({ ...form, amount: parseFloat(form.amount), campaign_ids: picked }) }),
    onSuccess: (res) => {
      if (res?.campaigns?.length) {
        toast.success(`Offering recorded · also counted towards ${res.campaigns.map(c => c.name).join(', ')}`);
        onSaved?.();
        return;
      }
      const r = res?.receipt;
      if (r && r.status === 'skipped') toast.warning(`Saved — receipt skipped: ${r.error}`);
      else if (r && r.status === 'failed') toast.warning(`Saved — receipt failed: ${r.error}`);
      else if (r && (r.status === 'sent' || r.status === 'queued')) toast.success(`Offering recorded · receipt ${r.status}`);
      else toast.success('Offering recorded');
      onSaved?.();
    },
    onError: (e) => toast.error(e.message),
  });
  const valid = parseFloat(form.amount) > 0;
  const needsPhone = form.send_receipt_via === 'sms' || form.send_receipt_via === 'whatsapp';
  const needsEmail = form.send_receipt_via === 'email';

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="offering-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Coins className="h-4 w-4 text-amber-600" />Record Offering</DialogTitle><DialogDescription>Logged against the church's offerings ledger. Optionally send a thank-you receipt.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div className="col-span-2"><Label>Amount *</Label><Input type="number" step="0.01" min={0} value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="offering-amount" /></div>
            <div><Label>Currency</Label>
              <CurrencySelect
                value={form.currency}
                onChange={v => setForm({ ...form, currency: v })}
                homeCurrency={homeCurrency}
                testId="offering-currency"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Donation type *</Label>
              <Select value={form.category} onValueChange={v => { setForm({ ...form, category: v }); setPicked([]); }}>
                <SelectTrigger data-testid="offering-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel className="text-[10px] uppercase tracking-wide text-slate-400">Standard types</SelectLabel>
                    {standardTypes.map(c => <SelectItem key={c} value={c} className="capitalize" data-testid={`offering-type-${c}`}>{c}</SelectItem>)}
                  </SelectGroup>
                  {campaignTypes.length > 0 && (
                    <SelectGroup>
                      <SelectLabel className="text-[10px] uppercase tracking-wide text-amber-600">Active campaigns</SelectLabel>
                      {campaignTypes.map(c => (
                        <SelectItem key={c.value} value={c.value} data-testid={`offering-type-${c.campaign_id}`}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
              {pickedCampaignType && (
                <p className="text-[10px] text-emerald-700 mt-0.5" data-testid="offering-campaign-type-note">
                  Recorded once on the church roll and counted towards this campaign.
                </p>
              )}
            </div>
            <div><Label>Method</Label>
              <Select value={form.payment_method} onValueChange={v => setForm({ ...form, payment_method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_METHODS.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Contributor</Label>
              {/* Pick the giver off the roll so the gift lands in their giving
                  history. Typing a name that is not on the roll still saves —
                  a visitor's gift is a real gift, and a blank is the anonymous
                  bulk offering. */}
              <MemberPicker
                churchId={churchId}
                api={api}
                value={{ member_id: form.member_id, name: form.contributor_name }}
                onChange={(m) => setForm({
                  ...form,
                  member_id: m.member_id || '',
                  contributor_name: m.name || '',
                  // Only fill contact details from the roll; never wipe
                  // something the recorder typed themselves.
                  contributor_email: m.email || form.contributor_email,
                  contributor_phone: m.phone || form.contributor_phone,
                })}
                placeholder="Leave blank for anonymous bulk"
                testId="offering-contributor"
              />
            </div>
            <div><Label>Contributor email</Label><Input type="email" value={form.contributor_email} onChange={e => setForm({ ...form, contributor_email: e.target.value })} placeholder="Optional" data-testid="offering-contributor-email" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Contributor phone</Label><Input value={form.contributor_phone} onChange={e => setForm({ ...form, contributor_phone: e.target.value })} placeholder="+27 82 123 4567" data-testid="offering-contributor-phone" /><p className="text-[10px] text-slate-400 mt-0.5">Required for SMS / WhatsApp receipts</p></div>
            <div><Label>Send receipt via</Label>
              <Select value={form.send_receipt_via} onValueChange={v => setForm({ ...form, send_receipt_via: v })}>
                <SelectTrigger data-testid="offering-receipt-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No receipt</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
              {needsPhone && !form.contributor_phone && <p className="text-[10px] text-amber-600 mt-0.5">Add a phone number to send via {form.send_receipt_via}.</p>}
              {needsEmail && !form.contributor_email && <p className="text-[10px] text-amber-600 mt-0.5">Add an email to send a receipt.</p>}
            </div>
          </div>
          <div><Label>Recorded for (date)</Label><Input type="date" value={form.recorded_for} onChange={e => setForm({ ...form, recorded_for: e.target.value })} /></div>
          <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>

          {!pickedCampaignType && (
            <CampaignPicker
              churchId={churchId}
              picked={picked}
              onToggle={toggleCampaign}
              onClear={() => setPicked([])}
            />
          )}

          {matches.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="offering-campaign-prompt">
              <p className="text-sm font-medium text-amber-900 flex items-center gap-1.5">
                <Megaphone className="h-4 w-4" />
                {matches.length === 1
                  ? 'This gift looks like it belongs to a campaign too'
                  : `${matches.length} live campaigns could match this gift`}
              </p>
              <p className="text-xs text-amber-800/80">
                Tick any it should also count towards. The church record stays exactly as it is —
                the amount is added to the campaign total as well.
              </p>
              <div className="space-y-1.5">
                {matches.map(m => (
                  <label key={m.id} className="flex items-start gap-2 text-sm text-amber-950 cursor-pointer"
                         data-testid={`offering-campaign-option-${m.id}`}>
                    <Checkbox checked={picked.includes(m.id)} onCheckedChange={() => toggleCampaign(m.id)} className="mt-0.5" />
                    <span className="min-w-0">
                      <span className="font-medium">{m.name}</span>
                      <span className="block text-[11px] text-amber-800/70">
                        matched on “{m.matched_on}” · {m.scope}
                        {m.target_amount > 0 && ` · ${m.currency} ${Number(m.raised_amount).toLocaleString()} of ${Number(m.target_amount).toLocaleString()}`}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mut.mutate()} disabled={!valid || mut.isPending} data-testid="offering-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ==================== SERMONS ====================
function SermonsTab({ churchId }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState('');
  const { data: sermons = [], isLoading } = useQuery({
    queryKey: ['church-sermons', churchId, q],
    queryFn: () => api(`/api/church/${churchId}/sermons?q=${encodeURIComponent(q)}`),
  });
  const del = useMutation({
    mutationFn: (id) => api(`/api/church/${churchId}/sermons/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['church-sermons', churchId] }); },
    onError: (e) => toast.error(e.message),
  });
  const togglePub = useMutation({
    mutationFn: ({ id, is_published }) => api(`/api/church/${churchId}/sermons/${id}`, { method: 'PUT', body: JSON.stringify({ is_published }) }),
    onSuccess: () => { toast.success('Updated'); qc.invalidateQueries({ queryKey: ['church-sermons', churchId] }); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-2 top-3 h-3.5 w-3.5 text-slate-400" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search title / speaker / scripture…" className="pl-8 h-9" data-testid="sermon-search" />
        </div>
        <Button onClick={() => setEditing({})} data-testid="sermon-add"><Plus className="h-4 w-4 mr-1" />New Sermon</Button>
      </div>
      {isLoading ? <div className="p-8 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>
       : sermons.length === 0 ? <Card><CardContent className="py-12 text-center text-slate-400"><Mic className="h-10 w-10 mx-auto mb-2" /><p>No sermons yet.</p></CardContent></Card>
       : <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {sermons.map(s => (
            <Card key={s.id} data-testid={`sermon-card-${s.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate flex items-center gap-2">
                      <Mic className="h-3.5 w-3.5 text-emerald-600" />{s.title}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{s.speaker || '—'} · {s.sermon_date}</p>
                  </div>
                  <Badge variant="outline" className={`text-[10px] ${s.is_published ? 'text-emerald-700 border-emerald-200 bg-emerald-50' : 'text-slate-500'}`}>
                    {s.is_published ? 'Published' : 'Draft'}
                  </Badge>
                </div>
                {s.scripture_ref && <p className="text-xs text-slate-600"><BookOpen className="h-3 w-3 inline mr-1" />{s.scripture_ref}</p>}
                {s.summary && <p className="text-xs text-slate-600 line-clamp-2">{s.summary}</p>}
                <div className="flex items-center gap-2 text-[11px] text-slate-400">
                  {s.audio_url && <span className="inline-flex items-center gap-1"><FileAudio className="h-3 w-3" />audio</span>}
                  {s.video_url && <span className="inline-flex items-center gap-1"><Video className="h-3 w-3" />video</span>}
                  {(s.tags || []).slice(0, 3).map(t => <Badge key={t} variant="outline" className="text-[9px]">#{t}</Badge>)}
                </div>
                <div className="flex gap-2 pt-2 border-t">
                  <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => setEditing(s)} data-testid={`sermon-edit-${s.id}`}><Edit3 className="h-3 w-3 mr-1" />Edit</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => togglePub.mutate({ id: s.id, is_published: !s.is_published })} data-testid={`sermon-toggle-${s.id}`}>{s.is_published ? 'Unpublish' : 'Publish'}</Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => { if (window.confirm('Delete sermon?')) del.mutate(s.id); }} data-testid={`sermon-del-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>}
      {editing && <SermonDialog churchId={churchId} sermon={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['church-sermons', churchId] }); setEditing(null); }} />}
    </div>
  );
}

function SermonDialog({ churchId, sermon, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    title: sermon?.title || '',
    speaker: sermon?.speaker || '',
    sermon_date: sermon?.sermon_date || today,
    scripture_ref: sermon?.scripture_ref || '',
    summary: sermon?.summary || '',
    notes: sermon?.notes || '',
    audio_url: sermon?.audio_url || '',
    video_url: sermon?.video_url || '',
    thumbnail_url: sermon?.thumbnail_url || '',
    tags: (sermon?.tags || []).join(', '),
    is_published: sermon?.is_published ?? true,
  });
  const mut = useMutation({
    mutationFn: () => {
      const body = JSON.stringify({ ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) });
      return sermon
        ? api(`/api/church/${churchId}/sermons/${sermon.id}`, { method: 'PUT', body })
        : api(`/api/church/${churchId}/sermons`, { method: 'POST', body });
    },
    onSuccess: () => { toast.success(sermon ? 'Sermon updated' : 'Sermon added'); onSaved?.(); },
    onError: (e) => toast.error(e.message),
  });
  const valid = form.title.trim() && form.sermon_date;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sermon-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mic className="h-4 w-4 text-emerald-600" />{sermon ? 'Edit Sermon' : 'New Sermon'}</DialogTitle>
          <DialogDescription>Published sermons appear in the church's public feed.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div className="col-span-2"><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="sermon-title" /></div>
            <div><Label>Date *</Label><Input type="date" value={form.sermon_date} onChange={e => setForm({ ...form, sermon_date: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Speaker</Label><Input value={form.speaker} onChange={e => setForm({ ...form, speaker: e.target.value })} placeholder="Pastor Smith" /></div>
            <div><Label>Scripture reference</Label><Input value={form.scripture_ref} onChange={e => setForm({ ...form, scripture_ref: e.target.value })} placeholder="John 3:16-21" /></div>
          </div>
          <div><Label>Summary <span className="text-xs text-slate-500 font-normal">(public)</span></Label><Textarea rows={2} value={form.summary} onChange={e => setForm({ ...form, summary: e.target.value })} placeholder="A short description for the public feed" /></div>
          <div className="grid grid-cols-2 gap-2">
            <MediaUpload value={form.audio_url} onChange={(url) => setForm({ ...form, audio_url: url })} purpose="audio" label="Audio" testId="sermon-audio-upload" />
            <div>
              <Label>Video URL</Label>
              <div className="flex items-center gap-1">
                <Input value={form.video_url} onChange={e => setForm({ ...form, video_url: e.target.value })} placeholder="https://…" data-testid="sermon-video-url" />
                <VideoOutputPicker
                  tenantId={churchId}
                  label=""
                  buttonSize="icon"
                  testIdPrefix="sermon-video-picker"
                  onPick={(url) => setForm(f => ({ ...f, video_url: url }))}
                />
              </div>
              <p className="text-xs text-slate-400 mt-0.5">Or pick a rendered clip from the Video Library</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <MediaUpload value={form.thumbnail_url} onChange={(url) => setForm({ ...form, thumbnail_url: url })} purpose="cover" label="Thumbnail" testId="sermon-thumbnail-upload" previewClassName="h-16" />
            <div><Label>Tags <span className="text-xs text-slate-500 font-normal">(comma-separated)</span></Label><Input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="grace, hope, youth" /></div>
          </div>
          <div><Label>Admin notes <span className="text-xs text-slate-500 font-normal">(not public)</span></Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          <label className="flex items-center gap-2 text-sm pt-2 border-t">
            <input type="checkbox" checked={form.is_published} onChange={e => setForm({ ...form, is_published: e.target.checked })} className="rounded" data-testid="sermon-published" />
            <span><span className="font-medium">Published</span><span className="text-xs text-slate-500 block">Visible in the church's public feed</span></span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => mut.mutate()} disabled={!valid || mut.isPending} data-testid="sermon-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{sermon ? 'Update' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ==================== CUSTOMIZE (THEME CMS) ====================
function CustomizeTab({ churchId, church }) {
  const qc = useQueryClient();
  const { data: theme, isLoading } = useQuery({
    queryKey: ['church-theme', churchId],
    queryFn: () => api(`/api/church/${churchId}/theme`),
  });
  const [draft, setDraft] = useState(null);
  const [providerInput, setProviderInput] = useState({ name: '', number: '' });
  React.useEffect(() => {
    if (theme && !draft) setDraft(JSON.parse(JSON.stringify(theme)));
  }, [theme, draft]);

  const save = useMutation({
    mutationFn: () => api(`/api/church/${churchId}/theme`, { method: 'PUT', body: JSON.stringify(draft) }),
    onSuccess: () => { toast.success('Customisation saved'); qc.invalidateQueries({ queryKey: ['church-theme', churchId] }); },
    onError: (e) => toast.error(e.message),
  });

  if (isLoading || !draft) return <div className="p-8 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>;

  const set = (path, value) => {
    setDraft(d => {
      const out = { ...d };
      const keys = path.split('.');
      let cur = out;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...cur[keys[i]] };
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return out;
    });
  };

  const sl = draft.social_links || {};
  const sec = draft.sections || {};
  const gs = draft.give_settings || {};
  const mm = gs.mobile_money || {};
  const providers = mm.providers || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-slate-500">Public page: <Link to={`/church/${church?.slug || ''}`} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline" data-testid="theme-public-link">/church/{church?.slug || ''}</Link></p>
        <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="theme-save">
          {save.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Save changes
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Branding</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Accent color</Label>
              <div className="flex gap-2 items-center">
                <Input type="color" className="h-10 w-16 p-1" value={draft.accent_color || '#0d9488'} onChange={e => set('accent_color', e.target.value)} data-testid="theme-accent-color" />
                <Input value={draft.accent_color || ''} onChange={e => set('accent_color', e.target.value)} placeholder="#0d9488" />
              </div>
            </div>
            <div><Label>Tagline</Label><Input value={draft.tagline || ''} onChange={e => set('tagline', e.target.value)} placeholder="e.g. Building Christ-centered families" data-testid="theme-tagline" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Secondary colour <span className="text-xs text-slate-500 font-normal">(buttons and highlights)</span></Label>
              <div className="flex gap-2 items-center">
                <Input type="color" className="h-10 w-16 p-1" value={draft.secondary_color || '#f59e0b'} onChange={e => set('secondary_color', e.target.value)} data-testid="theme-secondary-color" />
                <Input value={draft.secondary_color || ''} onChange={e => set('secondary_color', e.target.value)} placeholder="#f59e0b" />
              </div>
            </div>
            <div><Label>Public page style</Label>
              <p className="mt-2 text-xs text-slate-500">
                Every church page now follows the FWM house style — cream ground, serif
                headings and the gold accent. Your colours above tint the highlights.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[['hero_image_url', 'Banner image', 'theme-hero', 'banner',
               'Sits behind the headline at the top of your page. 1920 × 1080 works best. Nothing is laid over it — the picture shows as it is.'],
              ['hero_video_url', 'Banner video', 'theme-hero-video', 'video',
               'Optional. Plays silently behind the headline in place of the banner image, which becomes its still frame.'],
              ['logo_url', 'Profile / logo image', 'theme-logo', 'logo',
               'Shown in your page header and in the crest under the banner. A square 512 × 512 is ideal.']]
              .map(([key, label, testId, purpose, hint]) => (
              <div key={key} className="space-y-1">
                <MediaUpload value={draft[key] || ''} purpose={purpose} label={label} hint={hint}
                             testId={testId}
                             onChange={(url) => { set(key, url); if (url) toast.success('Uploaded — remember to save'); }} />
              </div>
            ))}
          </div>
          <div><Label>Welcome message <span className="text-xs text-slate-500 font-normal">(shown on public landing under the hero)</span></Label>
            <Textarea rows={2} value={draft.welcome_message || ''} onChange={e => set('welcome_message', e.target.value)} placeholder="A short welcome paragraph for visitors." />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Social links</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-3">
          {['facebook', 'instagram', 'youtube', 'tiktok', 'twitter', 'spotify'].map(p => (
            <div key={p}><Label className="capitalize">{p}</Label><Input value={sl[p] || ''} onChange={e => set(`social_links.${p}`, e.target.value)} placeholder="https://…" data-testid={`theme-social-${p}`} /></div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Sections on public page</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {[
            { k: 'services', label: 'Upcoming services' },
            { k: 'sermons',  label: 'Latest sermons' },
            { k: 'gallery',  label: 'Photo gallery' },
            { k: 'give',     label: 'Give / Donations' },
            { k: 'contact',  label: 'Contact info' },
          ].map(({ k, label }) => (
            <label key={k} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!sec[k]} onChange={e => set(`sections.${k}`, e.target.checked)} className="rounded" data-testid={`theme-section-${k}`} />
              <span>{label}</span>
            </label>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Coins className="h-4 w-4 text-amber-600" />Give settings</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!gs.enabled} onChange={e => set('give_settings.enabled', e.target.checked)} className="rounded" data-testid="theme-give-enabled" /><span>Giving enabled on public page</span></label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!gs.card_enabled} onChange={e => set('give_settings.card_enabled', e.target.checked)} className="rounded" data-testid="theme-give-card" /><span>Card (Stripe)</span></label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!gs.mobile_money_enabled} onChange={e => set('give_settings.mobile_money_enabled', e.target.checked)} className="rounded" data-testid="theme-give-mm" /><span>Mobile money</span></label>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><Label>Default currency</Label>
              <CurrencySelect
                value={gs.default_currency || 'USD'}
                onChange={v => set('give_settings.default_currency', v)}
                showHomeHint={false}
                testId="theme-give-currency"
              />
            </div>
            <div><Label>Min amount</Label><Input type="number" min={0} step="0.01" value={gs.min_amount ?? 1} onChange={e => set('give_settings.min_amount', parseFloat(e.target.value) || 0)} /></div>
            <div><Label>Max amount</Label><Input type="number" min={0} step="0.01" value={gs.max_amount ?? 10000} onChange={e => set('give_settings.max_amount', parseFloat(e.target.value) || 0)} /></div>
          </div>
          <div><Label>Suggested amounts <span className="text-xs text-slate-500 font-normal">(comma-separated)</span></Label>
            <Input
              value={(gs.suggested_amounts || []).join(', ')}
              onChange={e => set('give_settings.suggested_amounts', e.target.value.split(',').map(v => parseFloat(v.trim())).filter(v => v > 0))}
              placeholder="10, 25, 50, 100, 250"
              data-testid="theme-suggested"
            />
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3 mt-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Mobile money instructions</p>
            <Textarea
              rows={2}
              value={mm.instructions || ''}
              onChange={e => set('give_settings.mobile_money.instructions', e.target.value)}
              placeholder="e.g. Dial *151# on EcoCash, send to 0771 234 567, use your reference number."
              data-testid="theme-mm-instructions"
            />
            <div>
              <p className="text-xs text-slate-500 mb-1.5">Providers (shown to donor in a dropdown)</p>
              <div className="space-y-1.5">
                {providers.map((pr, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white border rounded px-2 py-1" data-testid={`theme-mm-provider-${i}`}>
                    <Badge variant="outline" className="text-[10px]">{pr.name}</Badge>
                    <span className="text-xs text-slate-600 flex-1 truncate">{pr.number}</span>
                    <Button size="icon" variant="ghost" className="h-6 w-6 text-rose-600" onClick={() => set('give_settings.mobile_money.providers', providers.filter((_, idx) => idx !== i))}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                <div className="flex gap-1">
                  <Input className="h-8" placeholder="Provider (EcoCash)" value={providerInput.name} onChange={e => setProviderInput({ ...providerInput, name: e.target.value })} />
                  <Input className="h-8" placeholder="Number / paybill" value={providerInput.number} onChange={e => setProviderInput({ ...providerInput, number: e.target.value })} />
                  <Button size="sm" variant="outline" onClick={() => {
                    if (!providerInput.name) return;
                    set('give_settings.mobile_money.providers', [...providers, { ...providerInput, display: `${providerInput.name} · ${providerInput.number}` }]);
                    setProviderInput({ name: '', number: '' });
                  }} data-testid="theme-mm-add-provider"><Plus className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}




// ==================== LIVE STREAMING ====================
function LiveTab({ churchId, church }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data: streams = [], isLoading } = useQuery({
    queryKey: ['church-streams', churchId],
    queryFn: () => api(`/api/church/${churchId}/streams`),
  });
  const goLive = useMutation({
    mutationFn: (id) => api(`/api/church/${churchId}/streams/${id}/go-live`, { method: 'POST' }),
    onSuccess: () => { toast.success('Marked as live'); qc.invalidateQueries({ queryKey: ['church-streams', churchId] }); },
    onError: (e) => toast.error(e.message),
  });
  const endIt = useMutation({
    mutationFn: (id) => api(`/api/church/${churchId}/streams/${id}/end`, { method: 'POST' }),
    onSuccess: () => { toast.success('Stream ended'); qc.invalidateQueries({ queryKey: ['church-streams', churchId] }); },
    onError: (e) => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: (id) => api(`/api/church/${churchId}/streams/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['church-streams', churchId] }); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <p className="text-sm text-slate-600">Schedule and broadcast services to multiple platforms.</p>
          <p className="text-xs text-slate-400 mt-1">Public live page: <Link to={`/churches/${church?.slug || ''}/live`} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline">/churches/{church?.slug || ''}/live <ExternalLink className="h-3 w-3 inline" /></Link></p>
        </div>
        <div className="flex gap-2">
          <Link to={`/admin/churches/${churchId}/go-live`}>
            <Button variant="outline" className="text-rose-700 border-rose-200" data-testid="open-go-live">
              <Radio className="h-4 w-4 mr-1" />Go Live from phone
            </Button>
          </Link>
          <Button onClick={() => setEditing({})} data-testid="stream-add"><Plus className="h-4 w-4 mr-1" />New Stream</Button>
        </div>
      </div>

      <BroadcastInfoEditor scope="church" ident={churchId} label="church" />

      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-4 text-xs text-amber-900 space-y-1.5">
          <p className="font-semibold flex items-center gap-1"><Radio className="h-3.5 w-3.5" />How this works</p>
          <ul className="list-disc list-inside space-y-0.5 text-amber-800">
            <li>Stream from OBS / your phone to YouTube, Facebook, Restream.io or your own RTMP server.</li>
            <li>Paste the <strong>watch link</strong> into Stream link — a YouTube, Facebook, Twitch or Vimeo
                address is turned into a player for you.</li>
            <li>An <strong>rtmp://</strong> address is where OBS sends video to. Viewers cannot play it, so paste
                the watch link the platform gives you instead.</li>
            <li>Click <strong>Go Live</strong> when broadcasting starts — or use <strong>Go Live from phone</strong>
                to start one in a single tap.</li>
          </ul>
        </CardContent>
      </Card>

      {isLoading ? <div className="p-8 text-center text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>
       : streams.length === 0 ? <Card><CardContent className="py-12 text-center text-slate-400"><Radio className="h-10 w-10 mx-auto mb-2" /><p>No streams yet. Schedule your first service.</p></CardContent></Card>
       : <div className="space-y-2">
          {streams.map(s => (
            <Card key={s.id} className={s.status === 'live' ? 'border-rose-400 bg-rose-50/40' : ''} data-testid={`stream-row-${s.id}`}>
              <CardContent className="p-3 flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate flex items-center gap-2">
                    <Radio className={`h-3.5 w-3.5 ${s.status === 'live' ? 'text-rose-600 animate-pulse' : 'text-slate-400'}`} />
                    {s.title}
                  </p>
                  <p className="text-xs text-slate-500">{s.scheduled_at?.slice(0, 16).replace('T', ' ')}{s.youtube_video_id && ` · YouTube ${s.youtube_video_id}`}</p>
                </div>
                <Badge variant="outline" className={`text-[10px] uppercase ${
                  s.status === 'live' ? 'text-rose-700 border-rose-200 bg-rose-50' :
                  s.status === 'scheduled' ? 'text-amber-700 border-amber-200 bg-amber-50' :
                  'text-slate-500'
                }`}>{s.status}</Badge>
                {s.status === 'scheduled' && <Button size="sm" variant="outline" className="h-7 text-xs text-rose-700" onClick={() => goLive.mutate(s.id)} data-testid={`stream-golive-${s.id}`}><Radio className="h-3 w-3 mr-1" />Go Live</Button>}
                {s.status === 'live' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => endIt.mutate(s.id)} data-testid={`stream-end-${s.id}`}>End</Button>}
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditing(s)} data-testid={`stream-edit-${s.id}`}><Edit3 className="h-3 w-3 mr-1" />Edit</Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={() => { if (window.confirm('Delete this stream?')) del.mutate(s.id); }} data-testid={`stream-del-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </CardContent>
              <div className="border-t border-slate-100 px-3 pb-3 pt-2">
                <BroadcastMethods scope="church" ident={s.id} compact />
              </div>
            </Card>
          ))}
        </div>
      }
      {editing && <StreamDialog churchId={churchId} stream={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { qc.invalidateQueries({ queryKey: ['church-streams', churchId] }); setEditing(null); }} />}
    </div>
  );
}

/** What a pasted stream link will actually do, checked by the same detector
 *  the public page uses, so the form cannot promise a player it will not get. */
function StreamUrlVerdict({ url }) {
  const [verdict, setVerdict] = useState(null);
  useEffect(() => {
    const trimmed = (url || '').trim();
    if (!trimmed) { setVerdict(null); return; }
    const timer = setTimeout(() => {
      api('/api/church/stream-url/preview', { method: 'POST', body: JSON.stringify({ url: trimmed }) })
        .then(setVerdict).catch(() => setVerdict(null));
    }, 400);
    return () => clearTimeout(timer);
  }, [url]);

  if (!verdict) return null;
  const playable = verdict.kind === 'iframe' || verdict.kind === 'video';
  return (
    <div className={`mt-2 rounded-md px-3 py-2 text-xs ${
      playable ? 'bg-emerald-50 text-emerald-900' : 'bg-amber-50 text-amber-900'}`}
      data-testid="stream-url-verdict">
      <p className="font-medium">
        {playable ? `Plays on your live page — ${verdict.label || verdict.platform}` : 'Will not play inside your page'}
      </p>
      {verdict.note && <p className="mt-0.5 opacity-80">{verdict.note}</p>}
    </div>
  );
}

function StreamDialog({ churchId, stream, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: stream?.title || '',
    scheduled_at: stream?.scheduled_at?.slice(0, 16) || new Date(Date.now() + 86400000).toISOString().slice(0, 16),
    description: stream?.description || '',
    stream_url: stream?.stream_url || '',
    youtube_video_id: stream?.youtube_video_id || '',
    embed_url: stream?.embed_url || '',
    facebook_url: stream?.facebook_url || '',
    tiktok_url: stream?.tiktok_url || '',
    twitch_url: stream?.twitch_url || '',
    restream_url: stream?.restream_url || '',
    custom_hls_url: stream?.custom_hls_url || '',
    rtmp_server_url: stream?.rtmp_server_url || '',
    rtmp_stream_key: stream?.rtmp_stream_key || '',
    self_hosted_enabled: stream?.self_hosted_enabled || false,
    thumbnail_url: stream?.thumbnail_url || '',
  });
  const mut = useMutation({
    mutationFn: () => {
      const body = JSON.stringify({ ...form, scheduled_at: new Date(form.scheduled_at).toISOString() });
      return stream
        ? api(`/api/church/${churchId}/streams/${stream.id}`, { method: 'PUT', body })
        : api(`/api/church/${churchId}/streams`, { method: 'POST', body });
    },
    onSuccess: () => { toast.success(stream ? 'Updated' : 'Stream scheduled'); onSaved?.(); },
    onError: (e) => toast.error(e.message),
  });
  const copy = (v) => { navigator.clipboard.writeText(v); toast.success('Copied'); };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="stream-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Radio className="h-4 w-4 text-rose-600" />{stream ? 'Edit Stream' : 'Schedule New Stream'}</DialogTitle>
          <DialogDescription>Connect OBS or your phone to broadcast — viewers watch on your public /live page.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <div className="col-span-2"><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="stream-title" /></div>
            <div><Label>Scheduled *</Label><Input type="datetime-local" value={form.scheduled_at} onChange={e => setForm({ ...form, scheduled_at: e.target.value })} /></div>
          </div>
          <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="A short note shown to viewers." /></div>

          <div className="border-t pt-3">
            <Label>Stream link</Label>
            <Input value={form.stream_url} onChange={e => setForm({ ...form, stream_url: e.target.value })}
                   placeholder="Paste from YouTube, Facebook, Twitch…"
                   data-testid="stream-url" />
            <StreamUrlVerdict url={form.stream_url} />
            <p className="text-xs text-slate-500 mt-1.5">
              Paste the watch link exactly as the platform gives it to you — it is turned into a
              player automatically. The fields below are still here if you prefer to set them yourself.
            </p>
          </div>

          <div className="border-t pt-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Public watch sources</p>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>YouTube video ID</Label><Input value={form.youtube_video_id} onChange={e => setForm({ ...form, youtube_video_id: e.target.value })} placeholder="e.g. dQw4w9WgXcQ" data-testid="stream-yt-id" /></div>
              <div><Label>Or full embed URL</Label><Input value={form.embed_url} onChange={e => setForm({ ...form, embed_url: e.target.value })} placeholder="https://…/embed/…" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div><Label>Facebook live URL</Label><Input value={form.facebook_url} onChange={e => setForm({ ...form, facebook_url: e.target.value })} placeholder="https://fb.com/…/live" /></div>
              <div><Label>TikTok live URL</Label><Input value={form.tiktok_url} onChange={e => setForm({ ...form, tiktok_url: e.target.value })} placeholder="https://tiktok.com/@…/live" /></div>
              <div><Label>Twitch URL</Label><Input value={form.twitch_url} onChange={e => setForm({ ...form, twitch_url: e.target.value })} placeholder="https://twitch.tv/…" /></div>
              <div><Label>Restream watch URL</Label><Input value={form.restream_url} onChange={e => setForm({ ...form, restream_url: e.target.value })} placeholder="https://watch.restream.io/…" /></div>
            </div>
            <div className="mt-2"><Label>Custom HLS URL <span className="text-xs text-slate-500 font-normal">(self-hosted .m3u8)</span></Label><Input value={form.custom_hls_url} onChange={e => setForm({ ...form, custom_hls_url: e.target.value })} placeholder="https://your-server/live.m3u8" /></div>
          </div>

          <div className="border-t pt-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Broadcaster credentials <span className="font-normal lowercase text-slate-400">(private — for OBS)</span></p>
            <div className="grid grid-cols-1 gap-2">
              <div>
                <Label>RTMP server URL</Label>
                <div className="flex gap-1"><Input value={form.rtmp_server_url} onChange={e => setForm({ ...form, rtmp_server_url: e.target.value })} placeholder="rtmp://a.rtmp.youtube.com/live2" />
                  {form.rtmp_server_url && <Button size="icon" variant="outline" className="h-9 w-9" onClick={() => copy(form.rtmp_server_url)}><Copy className="h-3.5 w-3.5" /></Button>}
                </div>
              </div>
              <div>
                <Label>Stream key</Label>
                <div className="flex gap-1"><Input type="password" value={form.rtmp_stream_key} onChange={e => setForm({ ...form, rtmp_stream_key: e.target.value })} placeholder="abcd-efgh-ijkl-mnop" />
                  {form.rtmp_stream_key && <Button size="icon" variant="outline" className="h-9 w-9" onClick={() => copy(form.rtmp_stream_key)}><Copy className="h-3.5 w-3.5" /></Button>}
                </div>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm mt-2">
              <input type="checkbox" checked={form.self_hosted_enabled} onChange={e => setForm({ ...form, self_hosted_enabled: e.target.checked })} className="rounded" />
              <span>Self-hosted RTMP server <span className="text-xs text-slate-500 block">(nginx-rtmp / Restream / SRS)</span></span>
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => { if (!form.title || !form.scheduled_at) return toast.error('Title + date required'); mut.mutate(); }} disabled={mut.isPending} data-testid="stream-save">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}{stream ? 'Update' : 'Schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default ChurchModule;
